import './signer-input-card.scss'

import Component from 'vue-class-component'
import { Watch } from 'vue-property-decorator'
import { faCheck, faEraser, faTimesCircle } from '@fortawesome/free-solid-svg-icons'

import { Signer, TranslatedString, getTranslatedString } from '@skinsoft/types'
import { thesaurusTermSelectors } from '@skinsoft/selectors'
import { ThesaurusTermDataStore } from '@skinsoft/entity'

import { ReactiveVue } from '@skinsoft/reactive-vue'
import { FreeDrawingTool } from '@skinsoft/entity-ui/src/condition-report/drawing-toolbox'
import { SAVE_CURRENT_REPORT_TOPIC } from '../../../../busTopics'
import { getCurrentDateAsSkinDate } from '@skinsoft/utils'

export interface SignatureDrawing {
    canvas: { width: number, height: number },
    signature: any[]
}

@Component({
    props: ['signer'],
    template: require('./signer-input-card.html')
})
export class SignerInputCard extends ReactiveVue {

    public static readonly DEFAULT_SIGNATURE_CANVAS_WIDTH = 700;
    public static readonly DEFAULT_SIGNATURE_CANVAS_HEIGHT = 300;

    public signer: Signer;
    public $refs: {
        stage: any;
    }

    private shapes: SignatureDrawing = {
        canvas: {
            height: SignerInputCard.DEFAULT_SIGNATURE_CANVAS_HEIGHT,
            width: SignerInputCard.DEFAULT_SIGNATURE_CANVAS_WIDTH
        }, signature: []
    };

    // Icons
    private eraseIcon = faEraser;
    private checkIcon = faCheck;
    private crossIcon = faTimesCircle;

    // Drawing data
    private readonly STROKE_WIDTH = 5;
    private readonly STROKE_COLOR = 'black';
    private configKonva = {
        height: SignerInputCard.DEFAULT_SIGNATURE_CANVAS_HEIGHT,
        preventDefault: true,
        width: SignerInputCard.DEFAULT_SIGNATURE_CANVAS_WIDTH,
    }
    private startClick: any = undefined;
    private currentShape: any = undefined;
    private freeDrawingTool: FreeDrawingTool = new FreeDrawingTool(this.STROKE_WIDTH, this.STROKE_COLOR);
    private isDrawing: boolean = false;
    private shapeIndex: number = 0;
    private signatureOk: boolean = false;

    private signerPosition: TranslatedString = {translatedString: {}}

    public mounted() {
        const stage = this.$refs.stage.getStage();
        const stageWidth = stage.container().clientWidth;
        stage.width(stageWidth);
        this.shapes.canvas.width = stageWidth
        this.fetchSignerPosition()
    }

    public get signerName() {
        return getTranslatedString(this.signer.signer, this.getAppLocale())
    }

    public fetchSignerPosition() {
        if(this.signer.positionOfSignatory) {
            return ThesaurusTermDataStore.getInstance().fetchOne(this.signer.positionOfSignatory)
                .subscribe(thesaurusTerm => {
                    this.signerPosition = thesaurusTermSelectors.preferredTerm(thesaurusTerm)
                })
        }
    }

    private saveSignature() {
        // only works because this.signer is an object
        // it is bad practice to change props directly, we should use an event
        this.signer.dateOfSignature = getCurrentDateAsSkinDate()
        this.emitOnBus(SAVE_CURRENT_REPORT_TOPIC)
    }

    private clearSignature() {
        if (!this.isDrawing) {
            // only works because this.signer is an object
            // it is bad practice to change props directly, we should use an event
            this.shapes = this.blankSignatureDrawing()
            this.signer.dateOfSignature = undefined
        }
    }

    private saveShapes() {
        // only works because this.signer is an object
        // it is bad practice to change props directly, we should use an event
        this.signer.dateOfSignature = getCurrentDateAsSkinDate()
        this.signer.signature = JSON.stringify(this.shapes);
    }

    // Drawing methods
    private onTouchStart(event) {
        event.evt.preventDefault()
        event.evt.stopPropagation()
        if (event.currentTarget._pointerPositions) {
            this.startClick = {}
            this.startClick.x = event.currentTarget._changedPointerPositions[0].x;
            this.startClick.y = event.currentTarget._changedPointerPositions[0].y;

            this.currentShape = this.freeDrawingTool.addShape(this.startClick.x, this.startClick.y, this.shapeIndex)
            this.shapes.signature.push(this.currentShape)
            this.shapeIndex++;
            this.isDrawing = true;
        } else {
            this.startClick = undefined
        }
    }

    private onTouchMove(event) {
        event.evt.preventDefault()
        event.evt.stopPropagation()
        if (this.isDrawing && event.currentTarget._changedPointerPositions) {

            const currentX = event.currentTarget._changedPointerPositions[0].x;
            const currentY = event.currentTarget._changedPointerPositions[0].y;

            this.freeDrawingTool.onTouchMove(this.currentShape, currentX, currentY, this.startClick.x, this.startClick.y);
        }
    }

    private onTouchEnd(event) {
        event.evt.preventDefault()
        event.evt.stopPropagation()
        if (this.startClick && event.currentTarget._changedPointerPositions) {

            const endX = event.currentTarget._changedPointerPositions[0].x;
            const endY = event.currentTarget._changedPointerPositions[0].y;
            this.freeDrawingTool.onTouchEnd(this.currentShape, endX, endY, this.startClick.x, this.startClick.y)
            this.currentShape = null;
            this.saveShapes()
        }
        this.isDrawing = false;
    }

    private blankSignatureDrawing(): SignatureDrawing {
        return {
            canvas: {
                height: SignerInputCard.DEFAULT_SIGNATURE_CANVAS_HEIGHT,
                width: SignerInputCard.DEFAULT_SIGNATURE_CANVAS_WIDTH,
            },
            signature: []
        } as SignatureDrawing
    }

    @Watch('signer', { immediate: true })
    private onSignerChange(signer) {
        this.shapes = signer.signature ? JSON.parse(signer.signature) : this.blankSignatureDrawing();
        this.shapeIndex = this.shapes.signature.length;
        this.fetchSignerPosition()
    }

    @Watch('shapes', { immediate: true, deep: true })
    private onShapesChange() {
        this.signatureOk = this.shapes.signature.length > 0
        this.saveShapes()
    }

}